import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CDSButton } from "@ciscodesignsystems/cds-react-button";
import { CDSCard } from "@ciscodesignsystems/cds-react-card";
import { CDSContainer } from "@ciscodesignsystems/cds-react-container";
import { CDSFlex } from "@ciscodesignsystems/cds-react-flex";
import { CDSNotification } from "@ciscodesignsystems/cds-react-notification";
import { CDSSkeleton } from "@ciscodesignsystems/cds-react-skeleton";
import { CDSTag } from "@ciscodesignsystems/cds-react-tag";
import { CDSTextArea } from "@ciscodesignsystems/cds-react-textarea";
import { CDSTextInput } from "@ciscodesignsystems/cds-react-text-input";
import { reviewSpec } from "../services/specReviewService";

const sampleSpecification = `openapi: 3.1.0
info:
  title: Orders API
  version: 1.0.0
paths:
  /orders:
    get:
      summary: List orders
      responses:
        "200":
          description: Success`;

const severityLabelMap = {
  error: "ERROR",
  warning: "WARNING",
  info: "INFO",
};

const severityClassMap = {
  error: "uag-review-finding--error",
  warning: "uag-review-finding--warning",
  info: "uag-review-finding--info",
};

const editorLineHeight = 24;
const reviewSpecStorageKey = "uag.reviewSpecPage";

const getDefaultSource = (fileName) => (fileName ? `upload://${fileName}` : "manual://review-spec");
const getEditorElement = (editorShellRef) => editorShellRef?.querySelector?.("textarea") || null;
const getLineNumberFromSelection = (value, selectionStart) =>
  value.slice(0, Math.max(selectionStart, 0)).split("\n").length;

const createTextSegment = (value, cursor) => ({
  type: "text",
  key: `text-${cursor}`,
  value,
});

const getLinkCandidate = (text, labelStart) => {
  const labelEnd = text.indexOf("]", labelStart + 1);
  const openParen = labelEnd === -1 ? -1 : text.indexOf("(", labelEnd + 1);
  const closeParen = openParen === -1 ? -1 : text.indexOf(")", openParen + 1);

  if (!(labelEnd > labelStart + 1 && openParen === labelEnd + 1 && closeParen > openParen + 1)) {
    return null;
  }

  return {
    labelEnd,
    closeParen,
    url: text.slice(openParen + 1, closeParen),
  };
};

const isSupportedLinkUrl = (url) => url.startsWith("http://") || url.startsWith("https://");

export const parseLinkedText = (value) => {
  const text = String(value || "");
  const segments = [];
  let cursor = 0;

  while (cursor < text.length) {
    const labelStart = text.indexOf("[", cursor);

    if (labelStart === -1) {
      segments.push(createTextSegment(text.slice(cursor), cursor));
      break;
    }

    const linkCandidate = getLinkCandidate(text, labelStart);

    if (!linkCandidate || !isSupportedLinkUrl(linkCandidate.url)) {
      segments.push(createTextSegment(text.slice(cursor), cursor));
      break;
    }

    if (labelStart > cursor) {
      segments.push(createTextSegment(text.slice(cursor, labelStart), cursor));
    }

    segments.push({
      type: "link",
      key: `link-${labelStart}-${linkCandidate.url}`,
      label: text.slice(labelStart + 1, linkCandidate.labelEnd),
      url: linkCandidate.url,
    });

    cursor = linkCandidate.closeParen + 1;
  }

  if (!segments.length) {
    return [createTextSegment(text, 0)];
  }

  return segments;
};

const renderLinkedText = (value) => {
  return parseLinkedText(value).map((segment) => {
    if (segment.type === "text") {
      return <Fragment key={segment.key}>{segment.value}</Fragment>;
    }

    return (
      <a
        key={segment.key}
        className="uag-review-reference-link"
        href={segment.url}
        target="_blank"
        rel="noreferrer"
      >
        {segment.label}
      </a>
    );
  });
};

const getInitialReviewState = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.localStorage?.getItem(reviewSpecStorageKey);

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);

    if (!parsedValue || typeof parsedValue !== "object") {
      return null;
    }

    return {
      specificationText:
        typeof parsedValue.specificationText === "string"
          ? parsedValue.specificationText
          : sampleSpecification,
      source:
        typeof parsedValue.source === "string" ? parsedValue.source : "manual://review-spec",
      searchValue: typeof parsedValue.searchValue === "string" ? parsedValue.searchValue : "",
      reviewSummary:
        parsedValue.reviewSummary && typeof parsedValue.reviewSummary === "object"
          ? {
              total: Number(parsedValue.reviewSummary.total || 0),
              errors: Number(parsedValue.reviewSummary.errors || 0),
              warnings: Number(parsedValue.reviewSummary.warnings || 0),
              info: Number(parsedValue.reviewSummary.info || 0),
            }
          : {
              total: 0,
              errors: 0,
              warnings: 0,
              info: 0,
            },
      findings: Array.isArray(parsedValue.findings) ? parsedValue.findings : [],
      activeLine: Number(parsedValue.activeLine || 0),
    };
  } catch (error) {
    return null;
  }
};

const ReviewSpecPage = () => {
  const [initialState] = useState(() => getInitialReviewState());
  const fileInputRef = useRef(null);
  const editorShellRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const [specificationText, setSpecificationText] = useState(
    initialState?.specificationText || sampleSpecification
  );
  const [source, setSource] = useState(initialState?.source || "manual://review-spec");
  const [searchValue, setSearchValue] = useState(initialState?.searchValue || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reviewSummary, setReviewSummary] = useState(
    initialState?.reviewSummary || {
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    }
  );
  const [findings, setFindings] = useState(initialState?.findings || []);
  const [activeLine, setActiveLine] = useState(initialState?.activeLine || 0);

  const visibleFindings = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    if (!normalizedQuery) {
      return findings;
    }

    return findings.filter((finding) =>
      [
        finding.title,
        finding.description,
        finding.suggestedFix,
        ...(finding.references || []).flatMap((reference) => [reference.title, reference.link]),
        String(finding.line || ""),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [findings, searchValue]);

  const specificationLines = useMemo(() => {
    const normalizedText = specificationText || "";
    return normalizedText.split("\n").length;
  }, [specificationText]);

  const emptyStateMessage = findings.length
    ? "No findings match the current search."
    : "Run a review to populate findings.";

  let findingsContent;

  if (isSubmitting) {
    findingsContent = (
      <div className="uag-review-loading-block" aria-label="Findings loading">
        <CDSSkeleton height="lg" lines={4} shade="regular" />
        <CDSSkeleton height="lg" lines={4} shade="regular" />
      </div>
    );
  } else if (visibleFindings.length) {
    findingsContent = visibleFindings.map((finding) => {
      const isInteractiveFinding = Boolean(finding.line);
      const findingKey = finding.id || `${finding.title}-${finding.line || "no-line"}`;
      const findingClassName = [
        "uag-review-finding",
        severityClassMap[finding.severity],
        isInteractiveFinding ? "uag-review-finding--interactive" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const findingSummary = (
        <CDSFlex direction="vertical" gap="xs">
          <CDSFlex className="uag-review-finding-header" gap="sm" align="center">
            <CDSTag>{severityLabelMap[finding.severity]}</CDSTag>
            <strong>{finding.title}</strong>
          </CDSFlex>
          <p>{finding.description}</p>
        </CDSFlex>
      );
      const suggestedFixContent = finding.suggestedFix ? (
        <p>
          <strong>Suggested fix:</strong> {renderLinkedText(finding.suggestedFix)}
        </p>
      ) : null;

      if (isInteractiveFinding) {
        return (
          <div key={findingKey} className={findingClassName}>
            <button
              type="button"
              className="uag-review-finding-action"
              aria-label={`${severityLabelMap[finding.severity]} ${finding.title}`}
              onClick={() => jumpToLine(finding.line)}
            >
              {findingSummary}
            </button>
            {suggestedFixContent}
          </div>
        );
      }

      return (
        <div key={findingKey} className={findingClassName}>
          <CDSFlex direction="vertical" gap="xs">
            {findingSummary}
            {suggestedFixContent}
          </CDSFlex>
        </div>
      );
    });
  } else {
    findingsContent = <div className="uag-review-empty-state">{emptyStateMessage}</div>;
  }

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleSpecChange = (event) => {
    setSpecificationText(event.target.value);
    setErrorMessage("");
  };

  const syncEditorScroll = (editorElement) => {
    const nextScrollTop = editorElement?.scrollTop || 0;

    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = nextScrollTop;
    }

  };

  const handleSpecScroll = (event) => {
    syncEditorScroll(event.target);
  };

  const syncActiveLineFromSelection = (editorElement) => {
    setActiveLine(getLineNumberFromSelection(editorElement.value || "", editorElement.selectionStart || 0));
  };

  const jumpToLine = (line) => {
    const editorElement = getEditorElement(editorShellRef.current);

    if (!line || !editorElement) {
      return;
    }

    const numericLine = Number(line);

    if (!Number.isFinite(numericLine) || numericLine < 1) {
      return;
    }

    const targetTop = Math.max((numericLine - 1) * editorLineHeight, 0);
    const lines = (editorElement.value || "").split("\n");
    const targetIndex = Math.max(
      lines.slice(0, Math.max(numericLine - 1, 0)).join("\n").length + (numericLine > 1 ? 1 : 0),
      0
    );
    const targetLineText = lines[Math.max(numericLine - 1, 0)] || "";
    const targetLineEnd = targetIndex + targetLineText.length;

    editorElement.focus();
    editorElement.setSelectionRange(targetIndex, targetLineEnd);
    editorElement.scrollTop = targetTop;
    syncActiveLineFromSelection(editorElement);

    window.requestAnimationFrame(() => {
      syncEditorScroll(editorElement);
    });
  };

  const handleUpload = async (event) => {
    const [file] = event.target.files || [];

    if (!file) {
      return;
    }

    const text = await file.text();
    setSpecificationText(text);
    setSource(getDefaultSource(file.name));
    setErrorMessage("");
    event.target.value = "";
  };

  const handleClear = () => {
    setSpecificationText("");
    setSource("manual://review-spec");
    setSearchValue("");
    setFindings([]);
    setActiveLine(0);
    setReviewSummary({
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    });
    setErrorMessage("");
  };

  const handleReview = async () => {
    if (!specificationText.trim()) {
      setErrorMessage("Paste or upload an OpenAPI specification before running a review.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setFindings([]);
    setActiveLine(0);
    setReviewSummary({
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    });

    try {
      const result = await reviewSpec({
        openapi: specificationText,
        source,
      });

      setReviewSummary(result.summary);
      setFindings(result.findings);
    } catch (error) {
      setErrorMessage(error.message || "Unable to review the specification.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    try {
      window.localStorage?.setItem(
        reviewSpecStorageKey,
        JSON.stringify({
          specificationText,
          source,
          searchValue,
          reviewSummary,
          findings,
          activeLine,
        })
      );
    } catch (error) {
      // Ignore storage failures (quota/private mode) so editing and review flows continue.
    }

    return undefined;
  }, [activeLine, findings, reviewSummary, searchValue, source, specificationText]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key !== reviewSpecStorageKey || !event.newValue) {
        return;
      }

      try {
        const nextState = JSON.parse(event.newValue);

        setSpecificationText(nextState.specificationText || "");
        setSource(nextState.source || "manual://review-spec");
        setSearchValue(nextState.searchValue || "");
        setReviewSummary(
          nextState.reviewSummary || {
            total: 0,
            errors: 0,
            warnings: 0,
            info: 0,
          }
        );
        setFindings(Array.isArray(nextState.findings) ? nextState.findings : []);
        setActiveLine(Number(nextState.activeLine || 0));
      } catch (error) {
        return;
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <CDSContainer className="uag-review-page">
      <CDSFlex className="uag-page-heading" direction="vertical">
        <h1>Review spec</h1>
        <p>
          Upload or paste your existing OpenAPI file to receive readable findings and
          suggested fixes.
        </p>
      </CDSFlex>

      <section className="uag-review-grid">
        <CDSCard className="uag-review-input-card">
          <CDSFlex direction="vertical" gap="md">
            <div>
              <h2>Specification input</h2>
            </div>

            <CDSFlex className="uag-review-actions" gap="sm" align="center">
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Specification file upload"
                accept=".yaml,.yml,.json,.txt"
                className="uag-review-file-input"
                onChange={handleUpload}
              />
              <CDSButton variant="secondary" onClick={handleOpenFilePicker}>
                Upload file
              </CDSButton>
              <CDSButton onClick={handleReview} disabled={isSubmitting}>
                {isSubmitting ? "Reviewing..." : "Review spec"}
              </CDSButton>
              <CDSButton variant="tertiary" onClick={handleClear} disabled={isSubmitting}>
                Clear
              </CDSButton>
            </CDSFlex>

            <div>
              <p id="review-spec-label" className="uag-form-label">
                OpenAPI specification
              </p>
              <div ref={editorShellRef} className="uag-review-editor-shell">
                <div
                  ref={lineNumbersRef}
                  className="uag-review-line-numbers"
                  aria-hidden="true"
                >
                  {Array.from({ length: specificationLines }, (_, index) => (
                    <span
                      key={`line-${index + 1}`}
                      className={activeLine === index + 1 ? "uag-review-line-number--active" : ""}
                    >
                      {index + 1}
                    </span>
                  ))}
                </div>

                <CDSTextArea
                  id="review-spec-input"
                  aria-label="OpenAPI specification"
                  aria-labelledby="review-spec-label"
                  width="auto"
                  value={specificationText}
                  onChange={handleSpecChange}
                  onScroll={handleSpecScroll}
                  onClick={(event) => syncActiveLineFromSelection(event.target)}
                  onKeyUp={(event) => syncActiveLineFromSelection(event.target)}
                  onSelect={(event) => syncActiveLineFromSelection(event.target)}
                  placeholder="Paste your OpenAPI YAML or JSON here"
                  rows={24}
                />
              </div>
            </div>

            {errorMessage ? (
              <CDSNotification
                className="uag-review-notification"
                title="Review request failed"
                description={errorMessage}
                sentiment="negative"
              />
            ) : null}
          </CDSFlex>
        </CDSCard>

        <CDSFlex direction="vertical" gap="md" className="uag-review-sidebar">
          <CDSCard className="uag-review-summary-card">
            <CDSFlex direction="vertical" gap="xs">
              <h2>Review summary</h2>
              {isSubmitting ? (
                <div className="uag-review-loading-block" aria-label="Review summary loading">
                  <CDSSkeleton height="md" lines={2} shade="regular" />
                </div>
              ) : (
                <CDSFlex className="uag-review-summary-row" gap="sm" wrap="wrap">
                  <span>{reviewSummary.total} total</span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--error">
                    {reviewSummary.errors} errors
                  </span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--warning">
                    {reviewSummary.warnings} warnings
                  </span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--info">
                    {reviewSummary.info} info
                  </span>
                </CDSFlex>
              )}
            </CDSFlex>
          </CDSCard>

          <CDSCard className="uag-review-findings-card">
            <CDSFlex direction="vertical" gap="md">
              <div>
                <h2>Findings</h2>
              </div>

              <CDSTextInput
                id="review-search"
                label="Search findings"
                aria-label="Search findings"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search findings"
              />

              <CDSFlex direction="vertical" gap="sm">
                {findingsContent}
              </CDSFlex>
            </CDSFlex>
          </CDSCard>
        </CDSFlex>
      </section>
    </CDSContainer>
  );
};

export default ReviewSpecPage;
